import {
  createCollection,
  localStorageCollectionOptions,
  useLiveQuery,
} from "@tanstack/react-db";
import { triggerDownload } from "@/data/export";
import {
  type Auto,
  type EndGame,
  type PreMatch,
  phaseOrder,
  type ScoutingPhase,
  type TeamReview,
  type Teleop,
} from "@/data/match";
import { addBreadcrumb, captureException } from "@/sentry";
import { shortDayName } from "@/util";

type ID = { id: string };

export type PhaseDataMap = {
  preMatch: PreMatch;
  auto: Auto;
  teleop: Teleop;
  endgame: EndGame;
  postMatch: TeamReview;
};

export type Match = {
  scouter: string;
  matchNumber: number;
  teamNumber: number;
  started: Date;
  finished?: Date;
  deleted?: boolean;
  phases: {
    [K in ScoutingPhase]?: PhaseDataMap[K];
  };
};

export const matchCollection = createCollection<Match & ID, string>(
  localStorageCollectionOptions({
    storageKey: "hs-matches",
    getKey: (item) => item.id,
  }),
);

export function resetCollections() {
  const ids = matchCollection.toArray.map((m) => m.id);
  for (const id of ids) {
    matchCollection.delete(id);
  }
}

export function softDeleteMatch(id: string) {
  if (!matchCollection.get(id)) {
    return;
  }
  matchCollection.update(id, (draft) => {
    draft.deleted = true;
  });
  addBreadcrumb({ category: "match", message: "Match deleted", data: { id } });
}

export function setScoutingPhaseData(
  id: string,
  phase: ScoutingPhase,
  data: PhaseDataMap[typeof phase],
) {
  if (!matchCollection.get(id)) {
    addBreadcrumb({
      category: "match",
      message: "Match not found for phase save",
      level: "warning",
      data: { id, phase },
    });
    return;
  }

  const isLastPhase = phase === phaseOrder[phaseOrder.length - 1];

  matchCollection.update(id, (draft) => {
    draft.phases = {
      ...draft.phases,
      [phase]: data,
    };
    if (isLastPhase) {
      draft.finished = new Date();
    }
  });
}

export function useMatch(id: string) {
  // TODO: Inefficient - TanStack DB lacks single-item reactive queries
  const { data } = useLiveQuery(() => matchCollection);
  return data?.find((m) => m.id === id);
}

export function newId(): string {
  return (
    Date.now().toString(36) +
    Math.random().toString(36).substring(2, 12).padStart(12, "0")
  );
}
// biome-ignore-start lint/style/noNonNullAssertion: We're finding all cases where finished != undefined, so safe to assume non-null
export function downloadMatches(del = false) {
  const matches = matchCollection.toArray
    .filter((m) => m.finished !== undefined && !m.deleted)
    .sort(
      (a, b) =>
        new Date(a.finished!).getTime() - new Date(b.finished!).getTime(),
    );

  if (matches.length === 0) {
    return;
  }

  const firstDay = shortDayName(matches[0].finished!);
  const lastDay = shortDayName(matches[matches.length - 1].finished!);

  // Strip the `deleted` field from exported data
  const exportData = matches.map(({ deleted, ...rest }) => rest);

  triggerDownload(
    JSON.stringify(exportData, null, 2),
    `matches_${firstDay}-${lastDay}_${Date.now()}.json`.toLowerCase(),
    "application/json",
  );

  addBreadcrumb({
    category: "data",
    message: "Matches exported",
    data: { count: matches.length, deleted: del },
  });

  if (del) {
    // Hard-delete exported matches and soft-deleted matches
    for (const match of matches) {
      matchCollection.delete(match.id);
    }
    for (const match of matchCollection.toArray.filter((m) => m.deleted)) {
      matchCollection.delete(match.id);
    }
  }
}
// biome-ignore-end lint/style/noNonNullAssertion: We're finding all cases where finished != undefined, so safe to assume non-null

export async function parseMatchesFile(file: File): Promise<Match[]> {
  const reader = new FileReader();
  reader.readAsText(file);

  return new Promise((resolve, reject) => {
    reader.onload = () => {
      try {
        const matches = JSON.parse(reader.result as string);
        resolve(matches);
      } catch (error) {
        captureException(error, { file: file.name, size: file.size });
        addBreadcrumb({
          category: "data",
          message: "Failed to parse matches file",
          level: "error",
          data: { file: file.name },
        });
        reject(error);
      }
    };
  });
}
